/**
 * Zahl und Einheit eines Datenpunkts für die Anzeige.
 *
 * **Stand aus der Gerätedetailseite hierher gezogen** (HM-053): Der Verlauf
 * zeigt dieselben Werte an Achsen, Kacheln und in einer Tabelle. Zwei Kopien
 * derselben Regel gehen beim ersten Ändern auseinander, und dann steht in der
 * Liste ein Zählerstand in kWh und im Diagramm daneben derselbe in MWh.
 *
 * **Umgerechnet wird ausschließlich in `@hausmeter/i18n`** — gespeichert wird in
 * Wh und W, kWh entsteht erst dort. Hier steht nur, welche Stufe gilt.
 *
 * **Eine Reihe hat eine gemeinsame Stufe, ein Einzelwert seine eigene.** Das ist
 * der Grund für den Umweg über einen Formatierer statt einer Funktion je Wert:
 * `formatValue` wählt die Größenordnung nach dem Betrag, und an einer Achse
 * stünde dann „1,1 MW" über „980 kW" — zwei Zahlen, die man nicht vergleichen
 * kann, obwohl genau das der Zweck einer Achse ist. Die Stufe wird deshalb
 * **einmal** aus einem Bezugswert bestimmt und dann für jeden Wert der Reihe
 * festgehalten.
 */
import type { ApiComponents } from "@hausmeter/sdk";
import { formatRatio, formatValue } from "@hausmeter/i18n";

type Datapoint = ApiComponents["schemas"]["Datapoint"];

export interface DatapointFormatter {
  /** Die Einheit, wie sie am Wert steht — „kWh", „W", „%", leer bei keiner. */
  readonly unit: string;
  /** Nur die Zahl — für eine Kolonne, in der die Einheit einmal oben steht. */
  readonly value: (value: number) => string;
  /** Zahl und Einheit zusammen. */
  readonly full: (value: number) => string;
  /**
   * Die Zahl für eine **Achsenkante**, kürzer gesetzt.
   *
   * **Ein Zählerstand sprengt sonst den linken Rand.** `11.049,197` braucht
   * mehr Platz, als neben der Achse ist, und wird abgeschnitten — die führende
   * Ziffer verschwindet, und aus elftausend werden tausend. Drei Kanten müssen
   * sich unterscheiden lassen, mehr nicht; die genaue Zahl steht darüber am
   * Wert und in der Tabelle darunter.
   */
  readonly axis: (value: number) => string;
}

/**
 * Der Formatierer für einen Datenpunkt.
 *
 * `reference` bestimmt die Größenordnung der ganzen Reihe — sinnvollerweise der
 * größte vorkommende Betrag. Für einen Einzelwert ist er der Wert selbst.
 *
 * **Ein Zählerstand bleibt in kWh, auch wenn er groß wird.** Die freie Wahl der
 * Größenordnung ist für einen Momentanwert richtig und für einen Stand falsch:
 * Aus 11.062.958,4 Wh würde „11,1 MWh" — auf dem Zähler stehen aber 11062,958
 * kWh, und wer sie vergleichen will, hat mit einer gerundeten Megawattstunde
 * nichts in der Hand. Erkannt wird der Stand an `monotonicity` und nicht an der
 * Rolle: Auch ein Zählwerk ohne gedeutete Rolle zählt aufwärts.
 *
 * **Ein Datenpunkt ohne Einheit bekommt keine erfunden.** Ein Schaltausgang,
 * eine Kennzahl, deren Einheit der Katalog nicht deuten konnte: Die nackte Zahl
 * ist die ehrliche Anzeige (Regel 9).
 */
export function datapointFormatter(point: Datapoint, reference: number): DatapointFormatter {
  if (point.unit === null) {
    const plain = new Intl.NumberFormat("de-DE", { maximumFractionDigits: 3 });
    const short = new Intl.NumberFormat("de-DE", { maximumFractionDigits: 1 });
    return {
      unit: "",
      value: (value) => plain.format(value),
      full: (value) => plain.format(value),
      axis: (value) => short.format(value),
    };
  }

  // `formatValue` hängt an einen Anteil ein Prozentzeichen, ohne ihn mit 100 zu
  // malnehmen; `formatRatio` ist die Funktion, die das tut.
  if (point.unit === "ratio") {
    return {
      unit: "%",
      value: (value) => formatRatio(value).text,
      full: (value) => formatRatio(value).full,
      // Das Prozentzeichen steht einmal über der Achse (`unit`), nicht an jeder Zahl.
      axis: (value) => new Intl.NumberFormat("de-DE", { maximumFractionDigits: 0 }).format(value * 100),
    };
  }

  // Als lokale Konstante, damit die Verengung auch in den Closures unten gilt:
  // `point.unit` ist für TypeScript in einer Funktion wieder `Unit | null`.
  const unit = point.unit;
  const counter = unit === "Wh" && point.monotonicity === "increasing";
  // Ein Strom von 0,06 A stünde mit einer Nachkommastelle als „0,1 A" da.
  const digits = counter ? 3 : unit === "A" ? 2 : undefined;

  /**
   * Die Stufe wird am Bezugswert bestimmt und danach festgehalten. Bei einem
   * Stand ist sie ohnehin fest — kWh, siehe oben.
   */
  const fixedUnit = counter
    ? "kWh"
    : formatValue(reference, unit, "de-DE", { maximumFractionDigits: digits }).unit;

  const options = { fixedUnit, maximumFractionDigits: digits };

  return {
    unit: fixedUnit,
    value: (value) => formatValue(value, unit, "de-DE", options).text,
    full: (value) => formatValue(value, unit, "de-DE", options).full,
    axis: (value) =>
      formatValue(value, unit, "de-DE", { fixedUnit, maximumFractionDigits: 1 }).text,
  };
}

/** Ein einzelner Wert, mit seiner eigenen Größenordnung. */
export function formatDatapointValue(value: number, point: Datapoint): string {
  return datapointFormatter(point, value).full(value);
}
